import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  BlockStack,
  InlineGrid,
  InlineStack,
  Text,
  Badge,
  Button,
  TextField,
  ChoiceList,
  Banner,
  Divider,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import {
  alegraRequest,
  detachInvoicePayments,
  invoiceDue,
  invoiceCreditable,
  issueCreditNote,
  cancelCreditNote,
} from "../../utility/invoicing";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate } from "../../utility/funcs";

// Single home for per-invoice actions: download PDF/XML, send by email, issue
// a nota de crédito, and cancel a CFDI. Cancel offers two paths: a plain cancel
// (motivo 02/03), or "cancelar y corregir" which hands off to the full invoice
// modal (via the onSubstitute callback) to re-issue a corrected substitute
// (motivo 01). The invoice's credit notes are listed with the same download /
// email actions, and can be cancelled.
//
// Props:
//   open, onClose
//   invoice  — { id, alegra_id, invoice_number, total, amount_paid,
//              amount_credited, status, stamped?, date | invoice_date,
//              recipient?, reservation_id?, reservation_ids? }
//   defaultEmail — prefill for "Enviar por correo" (looked up from the
//              invoice's client when empty)
//   onChanged — called after a cancel or a credit note so the parent can refresh
//   onSubstitute(invoice) — open the full corrected-invoice flow (optional)
function InvoiceActionsModal({ open, invoice, defaultEmail = "", onClose, onChanged, onSubstitute }) {
  const { session } = UserAuth();
  const [view, setView] = useState("main"); // main | email | cancel | credit | cancelCredit
  const [cancelMode, setCancelMode] = useState(null); // null (chooser) | plain
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const [email, setEmail] = useState(defaultEmail);
  const [emailSent, setEmailSent] = useState(false);
  // What "Enviar por correo" sends: the invoice, or one of its credit notes.
  const [emailDoc, setEmailDoc] = useState(null);

  const [motivo, setMotivo] = useState(["03"]);
  const [comentario, setComentario] = useState("");
  const [cancelled, setCancelled] = useState(false);

  const [creditNotes, setCreditNotes] = useState([]);
  const [hasReservation, setHasReservation] = useState(false);
  const [credited, setCredited] = useState(0);
  const [cnAmount, setCnAmount] = useState("");
  const [cnDescription, setCnDescription] = useState("");
  const [cnReason, setCnReason] = useState("");
  const [cnDiscounts, setCnDiscounts] = useState([]);
  const [cnIssued, setCnIssued] = useState(null);
  // Cancelling one of the invoice's credit notes.
  const [voidNote, setVoidNote] = useState(null);
  const [voidMotivo, setVoidMotivo] = useState(["02"]);
  const [voidReason, setVoidReason] = useState("");
  const [voidDone, setVoidDone] = useState(false);

  const loadCreditNotes = useCallback(async () => {
    if (!invoice?.id) return;
    const [{ data: notes }, { count }] = await Promise.all([
      supabase
        .from("credit_notes")
        .select("*")
        .eq("invoice_id", invoice.id)
        .order("created_at", { ascending: true }),
      supabase
        .from("invoice_reservations")
        .select("id", { count: "exact", head: true })
        .eq("invoice_id", invoice.id),
    ]);
    const list = notes || [];
    setCreditNotes(list);
    setCredited(
      list.filter((n) => n.status !== "void").reduce((s, n) => s + (n.total || 0), 0)
    );
    setHasReservation(!!invoice.reservation_id || (count || 0) > 0);
  }, [invoice?.id, invoice?.reservation_id]);

  useEffect(() => {
    if (open) {
      setView("main");
      setCancelMode(null);
      setErr(null);
      setBusy(null);
      setEmail(defaultEmail);
      setEmailSent(false);
      setEmailDoc(null);
      setMotivo(["03"]);
      setComentario("");
      setCancelled(false);
      setCreditNotes([]);
      setCredited(invoice?.amount_credited || 0);
      setCnIssued(null);
      loadCreditNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultEmail, invoice?.id]);

  if (!invoice) return null;
  const inv = { ...invoice, amount_credited: credited };
  const folio = inv.invoice_number || `#${inv.alegra_id || inv.id}`;
  const dateStr = inv.date || inv.invoice_date;
  const stamped = inv.stamped ?? true;
  const isVoid = inv.status === "void" || cancelled;
  const hasPayments = (inv.amount_paid || 0) > 0.01;
  const activeNotes = creditNotes.filter((n) => n.status !== "void");
  const due = Math.max(0, invoiceDue(inv));
  const creditable = Math.max(0, invoiceCreditable(inv));

  const openAlegraFile = async (field, doc = null) => {
    const key = doc ? `${field}-${doc.id}` : field;
    setBusy(key);
    setErr(null);
    try {
      const path = doc ? `credit-notes/${doc.alegra_id}` : `invoices/${inv.alegra_id}`;
      const data = await alegraRequest(`${path}?fields=${field}`, "GET");
      const url = data?.[field];
      if (url) window.open(url, "_blank");
      else setErr(`No se encontró el ${field.toUpperCase()}.`);
    } catch (e) {
      setErr(e.message || `Error al obtener el ${field.toUpperCase()}`);
    } finally {
      setBusy(null);
    }
  };

  // The client's email on file, for pages that don't pass defaultEmail.
  const openEmail = async (doc = null) => {
    setEmailDoc(doc);
    setEmailSent(false);
    setView("email");
    if (email.trim()) return;
    const { data } = await supabase
      .from("invoices_issued")
      .select("customers(email), companies(contact_email)")
      .eq("id", inv.id)
      .maybeSingle();
    const found = data?.companies?.contact_email || data?.customers?.email || "";
    if (found) setEmail((prev) => prev || found);
  };

  const sendEmail = async () => {
    if (!email.trim()) return;
    setBusy("email");
    setErr(null);
    try {
      const path = emailDoc ? `credit-notes/${emailDoc.alegra_id}` : `invoices/${inv.alegra_id}`;
      await alegraRequest(`${path}/email`, "POST", {
        emails: [email.trim()],
        sendCopyToUser: false,
      });
      setEmailSent(true);
    } catch (e) {
      setErr(e.message || "Error al enviar el correo");
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async () => {
    const cause = motivo[0];
    setBusy("cancel");
    setErr(null);
    try {
      // Payments must be detached before Alegra will accept the void.
      if (hasPayments) await detachInvoicePayments(inv.alegra_id);
      await alegraRequest(`invoices/${inv.alegra_id}/void`, "POST", { cause });
      const { error } = await supabase.rpc("cancel_invoice", {
        p_invoice_id: inv.id,
        p_motivo: cause,
        p_reason: comentario.trim() || null,
        p_user: session?.user?.id || null,
      });
      if (error) throw new Error(error.message);
      setCancelled(true);
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.message || "Error al cancelar la factura");
    } finally {
      setBusy(null);
    }
  };

  const startSubstitute = () => {
    if (onSubstitute) {
      onSubstitute(inv);
      onClose();
    }
  };

  const startCredit = () => {
    setCnAmount((due > 0.01 ? due : creditable).toFixed(2));
    setCnDescription(`Descuento sobre factura ${folio}`);
    setCnReason("");
    setCnDiscounts(hasReservation ? [] : ["no"]);
    setCnIssued(null);
    setErr(null);
    setView("credit");
  };

  const cnTotal = Math.round((parseFloat(cnAmount) || 0) * 100) / 100;
  const cnInvalid =
    !(cnTotal > 0) || cnTotal > creditable + 0.005 || !cnDescription.trim() || cnDiscounts.length === 0;
  // Part of the credit note the invoice's balance doesn't cover: it comes off
  // the payments and stays as the customer's saldo a favor.
  const cnFreesPayment = Math.max(0, Math.round((cnTotal - due) * 100) / 100);

  const startVoidNote = (n) => {
    setVoidNote(n);
    setVoidMotivo(["02"]);
    setVoidReason("");
    setVoidDone(false);
    setErr(null);
    setView("cancelCredit");
  };

  const doVoidNote = async () => {
    setBusy("voidNote");
    setErr(null);
    try {
      await cancelCreditNote({ note: voidNote, motivo: voidMotivo[0], reason: voidReason });
      setVoidDone(true);
      await loadCreditNotes();
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.message || "Error al cancelar la nota de crédito");
    } finally {
      setBusy(null);
    }
  };

  const doCredit = async () => {
    setBusy("credit");
    setErr(null);
    try {
      const note = await issueCreditNote({
        invoice: inv,
        amount: cnTotal,
        description: cnDescription,
        reason: cnReason,
        discountsReservation: cnDiscounts[0] === "yes",
      });
      setCnIssued(note);
      await loadCreditNotes();
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.message || "Error al emitir la nota de crédito");
    } finally {
      setBusy(null);
    }
  };

  const summary = (
    <BlockStack gap="100">
      <InlineGrid columns="1fr auto" alignItems="center">
        <Text variant="headingMd">{folio}</Text>
        {isVoid ? (
          <Badge tone="critical">Cancelada</Badge>
        ) : due <= 0.01 ? (
          <Badge tone="success">Pagada</Badge>
        ) : hasPayments || credited > 0.01 ? (
          <Badge tone="attention">Pago parcial</Badge>
        ) : (
          <Badge tone="warning">Por cobrar</Badge>
        )}
      </InlineGrid>
      <Text tone="subdued" variant="bodySm">
        {inv.recipient ? `${inv.recipient} · ` : ""}
        {dateStr ? `${formatDate(dateStr)} · ` : ""}
        Total {formatPrice(inv.total || 0)}
        {hasPayments ? ` · Pagado ${formatPrice(inv.amount_paid || 0)}` : ""}
        {credited > 0.01 ? ` · Notas de crédito ${formatPrice(credited)}` : ""}
      </Text>
    </BlockStack>
  );

  const creditNoteList = creditNotes.length > 0 && (
    <BlockStack gap="200">
      <Text variant="headingSm">Notas de crédito</Text>
      {creditNotes.map((n) => (
        <InlineGrid key={n.id} columns="1fr auto" alignItems="center" gap="200">
          <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center">
              <Text fontWeight="semibold">NC {n.number || n.alegra_id}</Text>
              {n.status === "void" && <Badge tone="critical">Cancelada</Badge>}
            </InlineStack>
            <Text tone="subdued" variant="bodySm">
              {formatDate(n.date)} · {formatPrice(n.total)}
              {n.description ? ` · ${n.description}` : ""}
            </Text>
          </BlockStack>
          <InlineStack gap="100" wrap={false}>
            <Button size="slim" loading={busy === `pdf-${n.id}`} onClick={() => openAlegraFile("pdf", n)}>
              PDF
            </Button>
            <Button size="slim" loading={busy === `xml-${n.id}`} onClick={() => openAlegraFile("xml", n)}>
              XML
            </Button>
            <Button size="slim" onClick={() => openEmail(n)}>
              Enviar
            </Button>
            {n.status !== "void" && view === "main" && (
              <Button size="slim" tone="critical" variant="plain" onClick={() => startVoidNote(n)}>
                Cancelar
              </Button>
            )}
          </InlineStack>
        </InlineGrid>
      ))}
    </BlockStack>
  );

  let body;
  if (view === "main") {
    body = (
      <BlockStack gap="400">
        {summary}
        <Divider />
        <InlineStack gap="200" wrap>
          <Button loading={busy === "pdf"} onClick={() => openAlegraFile("pdf")}>
            Descargar PDF
          </Button>
          {stamped && (
            <Button loading={busy === "xml"} onClick={() => openAlegraFile("xml")}>
              Descargar XML
            </Button>
          )}
          <Button onClick={() => openEmail(null)}>Enviar por correo</Button>
          {!isVoid && stamped && creditable > 0.01 && (
            <Button onClick={startCredit}>Nota de crédito</Button>
          )}
          {!isVoid && (
            <Button tone="critical" onClick={() => { setCancelMode(null); setView("cancel"); }}>
              Cancelar factura
            </Button>
          )}
        </InlineStack>
        {isVoid && (
          <Banner tone="warning">
            Esta factura está cancelada. La cancelación ante el SAT puede tardar si
            el cliente debe aceptarla.
          </Banner>
        )}
        {creditNoteList && (
          <>
            <Divider />
            {creditNoteList}
          </>
        )}
      </BlockStack>
    );
  } else if (view === "email") {
    const docLabel = emailDoc ? `Nota de crédito ${emailDoc.number || emailDoc.alegra_id}` : "Factura";
    body = (
      <BlockStack gap="400">
        {summary}
        <Divider />
        {emailSent ? (
          <Banner tone="success">{docLabel} enviada a {email}.</Banner>
        ) : (
          <>
            {emailDoc && <Text>{docLabel} · {formatPrice(emailDoc.total)}</Text>}
            <TextField label="Enviar a" type="email" value={email} onChange={setEmail} autoComplete="off" placeholder="correo@cliente.com" />
          </>
        )}
      </BlockStack>
    );
  } else if (view === "credit") {
    body = (
      <BlockStack gap="400">
        {summary}
        <Divider />
        {cnIssued ? (
          <BlockStack gap="300">
            <Banner tone="success">
              Nota de crédito {cnIssued.number} timbrada por {formatPrice(cnIssued.total)}.
              {cnIssued.payment_freed > 0.005 &&
                ` ${formatPrice(cnIssued.payment_freed)} del pago quedó sin aplicar como saldo a favor del cliente.`}
            </Banner>
            {creditNoteList}
          </BlockStack>
        ) : (
          <>
            <TextField
              label="Monto (IVA incluido)"
              type="number"
              prefix="$"
              value={cnAmount}
              onChange={setCnAmount}
              autoComplete="off"
              helpText={
                creditable > due + 0.005
                  ? `La factura debe ${formatPrice(due)}; se le pueden acreditar hasta ${formatPrice(creditable)}.`
                  : `La factura debe ${formatPrice(due)}; la nota de crédito no puede ser mayor.`
              }
              error={cnTotal > creditable + 0.005 ? `Máximo ${formatPrice(creditable)}` : undefined}
            />
            {cnFreesPayment > 0.005 && cnTotal <= creditable + 0.005 && (
              <Banner tone="attention">
                {formatPrice(cnFreesPayment)} ya están pagados: se quitan del pago y quedan sin
                aplicar como saldo a favor del cliente, para devolverlos o aplicarlos a otra factura.
              </Banner>
            )}
            <TextField
              label="Concepto"
              value={cnDescription}
              onChange={setCnDescription}
              autoComplete="off"
              helpText="Aparece como el concepto del CFDI."
            />
            <TextField
              label="Nota en el PDF (opcional)"
              value={cnReason}
              onChange={setCnReason}
              multiline={2}
              autoComplete="off"
            />
            {hasReservation && (
              <ChoiceList
                title="¿Qué corrige esta nota de crédito?"
                choices={[
                  {
                    label: "Un descuento o bonificación: el cliente deja de deber este monto",
                    value: "yes",
                    helpText: "También se descuenta del total de la reservación.",
                  },
                  {
                    label: "La factura cobró de más: la reservación ya estaba bien",
                    value: "no",
                    helpText: "Sólo baja el saldo de la factura.",
                  },
                ]}
                selected={cnDiscounts}
                onChange={setCnDiscounts}
              />
            )}
          </>
        )}
      </BlockStack>
    );
  } else if (view === "cancelCredit" && voidNote) {
    body = (
      <BlockStack gap="400">
        {summary}
        <Divider />
        {voidDone ? (
          <Banner tone="success">
            NC {voidNote.number || voidNote.alegra_id} cancelada.
          </Banner>
        ) : (
          <>
            <Text>
              NC {voidNote.number || voidNote.alegra_id} · {formatDate(voidNote.date)} ·{" "}
              {formatPrice(voidNote.total)}
            </Text>
            <ChoiceList
              title="Motivo"
              choices={[
                { label: "Se emitió con un error", value: "02" },
                { label: "La operación no se llevó a cabo", value: "03" },
              ]}
              selected={voidMotivo}
              onChange={setVoidMotivo}
            />
            <TextField label="Comentario (opcional)" value={voidReason} onChange={setVoidReason} multiline={2} autoComplete="off" />
            <Banner tone="attention">
              La factura vuelve a deber {formatPrice(voidNote.total)}.
              {voidNote.discounts_reservation && " El descuento a la reservación se quita."}
              {voidNote.payment_freed > 0.005 &&
                ` Los ${formatPrice(voidNote.payment_freed)} que se liberaron del pago no se vuelven a aplicar solos.`}
            </Banner>
          </>
        )}
      </BlockStack>
    );
  } else {
    // cancel
    body = (
      <BlockStack gap="400">
        {summary}
        <Divider />
        {cancelled ? (
          <Banner tone="success">
            Cancelación solicitada. Si el cliente debe aceptarla, la factura quedará
            “en proceso de cancelación” hasta que el SAT la confirme.
          </Banner>
        ) : activeNotes.length > 0 ? (
          <Banner tone="warning">
            Esta factura tiene notas de crédito. El SAT no permite cancelarla mientras
            estén vigentes: cancélalas primero desde la lista de notas de crédito.
          </Banner>
        ) : cancelMode === "plain" ? (
          <>
            <Text variant="headingSm">¿Por qué se cancela?</Text>
            <ChoiceList
              title="Motivo"
              titleHidden
              choices={[
                { label: "La operación no se realizó (la renta o cargo no sucedió)", value: "03" },
                { label: "Se emitió con un error y no se va a re-facturar", value: "02" },
              ]}
              selected={motivo}
              onChange={setMotivo}
            />
            <TextField label="Comentario (opcional)" value={comentario} onChange={setComentario} multiline={2} autoComplete="off" />
            {hasPayments && (
              <Banner tone="attention">
                Esta factura tiene {formatPrice(inv.amount_paid || 0)} en pagos. Al
                cancelar, ese dinero quedará disponible (sin aplicar) en la
                reservación para usarlo después.
              </Banner>
            )}
          </>
        ) : (
          <BlockStack gap="300">
            <Text>¿Cómo quieres cancelar esta factura?</Text>
            <Button onClick={() => setCancelMode("plain")}>
              Solo cancelar (no se va a re-facturar)
            </Button>
            {onSubstitute && (
              <Button onClick={startSubstitute}>
                Cancelar y corregir (emitir una factura nueva que la sustituye)
              </Button>
            )}
          </BlockStack>
        )}
      </BlockStack>
    );
  }

  let primary;
  let secondary = [];
  if (view === "main") {
    primary = { content: "Cerrar", onAction: onClose };
  } else if (view === "email") {
    primary = emailSent
      ? { content: "Listo", onAction: () => setView("main") }
      : { content: "Enviar", onAction: sendEmail, loading: busy === "email", disabled: !email.trim() };
    if (!emailSent) secondary = [{ content: "Volver", onAction: () => setView("main") }];
  } else if (view === "cancelCredit") {
    if (voidDone) {
      primary = { content: "Listo", onAction: () => setView("main") };
    } else {
      primary = {
        content: "Cancelar nota de crédito",
        destructive: true,
        onAction: doVoidNote,
        loading: busy === "voidNote",
      };
      secondary = [{ content: "Volver", onAction: () => setView("main") }];
    }
  } else if (view === "credit") {
    if (cnIssued) {
      primary = { content: "Listo", onAction: () => setView("main") };
    } else {
      primary = {
        content: "Timbrar nota de crédito",
        onAction: doCredit,
        loading: busy === "credit",
        disabled: cnInvalid,
      };
      secondary = [{ content: "Volver", onAction: () => setView("main") }];
    }
  } else {
    if (cancelled) {
      primary = { content: "Listo", onAction: onClose };
    } else if (cancelMode === "plain" && activeNotes.length === 0) {
      primary = { content: "Cancelar factura", destructive: true, onAction: doCancel, loading: busy === "cancel" };
      secondary = [{ content: "Volver", onAction: () => setCancelMode(null) }];
    } else {
      primary = { content: "Cerrar", onAction: () => setView("main") };
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={view === "credit" || view === "cancelCredit" ? "Nota de crédito" : "Factura"}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {err && (
            <Banner tone="critical" onDismiss={() => setErr(null)}>
              {err}
            </Banner>
          )}
          {body}
        </BlockStack>
      </Modal.Section>
      <Modal.Section>
        <InlineStack align="end" gap="200">
          {secondary.map((a) => (
            <Button
              key={a.content}
              onClick={a.onAction}
              disabled={busy === "cancel" || busy === "credit" || busy === "voidNote"}
            >
              {a.content}
            </Button>
          ))}
          <Button
            variant="primary"
            tone={primary.destructive ? "critical" : undefined}
            onClick={primary.onAction}
            loading={primary.loading}
            disabled={primary.disabled}
          >
            {primary.content}
          </Button>
        </InlineStack>
      </Modal.Section>
    </Modal>
  );
}

export default InvoiceActionsModal;
